import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Avatar,
  Checkbox,
  DeskButton,
  DeskLink,
  DeskModal,
  Eyebrow,
  Field,
  PLATFORM_LABELS,
  ROLE_LABELS,
  SectionHead,
  STAGES,
  STAGE_LABELS,
  TYPE_LABELS,
  TYPE_FULL_LABELS,
  dateShort,
  daysUntil,
  essayTitle,
  initialsOf,
  NotifyToggle,
  inputStyle,
  personColor,
  relativeDay,
  stageIndex,
} from './essayShared';

// The essay detail screen, rendered the same way for whoever is looking at it.
// The student sees all of it; a parent or an advisor sees the same page with
// the parts their access doesn't cover left out. What each viewer may do comes
// in through `can` and `actions` rather than being decided in here, so there is
// one layout to keep faithful to the design instead of two that drift.

export const ESSAY_DOC_ROLES = [
  { value: 'DRAFT', label: 'Draft', description: 'Working document' },
  { value: 'OUTLINE', label: 'Outline', description: 'Structural outline' },
  { value: 'BRAINSTORM', label: 'Brainstorm', description: 'Ideas and notes' },
  { value: 'FEEDBACK', label: 'Feedback', description: 'Teacher / peer feedback' },
  { value: 'NOTES', label: 'Notes', description: 'Miscellaneous notes' },
  { value: 'FINAL', label: 'Final', description: 'Submitted version' },
];

const WAITING_OPTIONS = [
  { value: 'NONE', label: 'Nobody — it is moving' },
  { value: 'STUDENT', label: 'You' },
  { value: 'ADVISOR', label: 'Your advisor' },
  { value: 'PARENT', label: 'A parent' },
];

const TASK_STATUS_LABELS = {
  TODO: 'To do',
  IN_PROGRESS: 'In progress',
  DONE: 'Done',
  DECLINED: 'Declined',
};

const TASK_COLUMNS = '20px minmax(0,1fr) 150px 96px 104px';

const isOpen = (task) => task.status !== 'DONE' && task.status !== 'DECLINED';

const samePerson = (a, b) => !!a && !!b && a.kind === b.kind && a.id === b.id;

// A note posted in the last three days by somebody else is still "new" to you.
const isFresh = (note, viewer) =>
  !samePerson(note.author, viewer) && Date.now() - new Date(note.createdAt) < 3 * 24 * 60 * 60 * 1000;

export default function EssayDetailView({
  essay,
  docs = [],
  people = [],
  viewer,
  can = {},
  actions = {},
  breadcrumb = [],
  docPickerSlot = null,
  emailSlot = null,
}) {
  const [editing, setEditing] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const [taskFilter, setTaskFilter] = useState('ALL');

  const notes = useMemo(() => essay?.brainstormNotes || [], [essay]);
  const topLevelNotes = useMemo(() => notes.filter((n) => !n.parentId), [notes]);
  const repliesByParent = useMemo(() => {
    const map = new Map();
    notes
      .filter((n) => n.parentId)
      .forEach((n) => {
        if (!map.has(n.parentId)) map.set(n.parentId, []);
        map.get(n.parentId).push(n);
      });
    return map;
  }, [notes]);

  const noteAuthorCount = useMemo(
    () => new Set(notes.map((n) => `${n.author?.kind}:${n.author?.id}`)).size,
    [notes]
  );

  // Who an email would go to, said plainly next to the box that sends it.
  const notifyWho = (can.viewerIsOwner
    ? people.filter((p) => !p.isYou && p.comment !== false && p.role !== 'STUDENT')
    : people.filter((p) => !p.isYou && (p.role === 'STUDENT' || p.comment))
  ).map((p) => p.name);

  // You aren't "shared with" yourself — the student's card lists the others.
  // Everybody else's card is "who is on this", so it includes them.
  const sidebarPeople = can.viewerIsOwner ? people.filter((p) => !p.isYou) : people;

  const tasks = essay?.tasks || [];
  const myTasks = tasks.filter((t) => (t.assignees || []).some((a) => samePerson(a, viewer)));
  const shownTasks = taskFilter === 'MINE' ? myTasks : tasks;
  const openCount = tasks.filter(isOpen).length;
  const doneCount = tasks.length - openCount;

  const school = essay.collegeApplication?.collegeName || essay.school;
  const deadline = essay.collegeApplication?.deadline || null;
  const currentDoc = docs.find((d) => d.isCurrent) || docs[0] || null;
  const draftUrl = currentDoc?.url || (can.readDrafts !== false ? essay.googleDocsUrl : null) || null;
  const stage = stageIndex(essay);
  const checklistDone = (essay.checklistItems || []).filter((i) => i.completed).length;

  // "Waiting on you" only reads as *you* to the person it's waiting on.
  const waitingCopy = (() => {
    if (!essay.waitingOn || essay.waitingOn === 'NONE') return null;
    if (essay.waitingNote) return essay.waitingNote;
    const owner = can.viewerIsOwner;
    if (essay.waitingOn === 'STUDENT') return owner ? 'You' : `${people[0]?.name || 'The student'}`;
    if (essay.waitingOn === 'ADVISOR') return viewer?.role === 'ADVISOR' ? 'You' : 'Their advisor';
    return viewer?.role === 'PARENT' ? 'You' : 'A parent';
  })();

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '30px 32px 64px' }}>
      <style>{`
        @media (max-width: 1000px) {
          .desk-detail-grid { grid-template-columns: minmax(0,1fr) !important; }
          .desk-stage-strip { flex-wrap: wrap; }
          .desk-stage-strip > div { min-width: 160px; }
        }
      `}</style>

      {/* ── Breadcrumb ────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: 'var(--college-muted)',
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        {breadcrumb.map((crumb, i) => (
          <React.Fragment key={`${crumb.label}-${i}`}>
            {i > 0 && <span style={{ color: 'var(--college-border)' }}>/</span>}
            {crumb.to ? (
              <Link to={crumb.to} style={{ color: 'var(--college-muted)', textDecoration: 'none' }}>
                {crumb.label}
              </Link>
            ) : (
              <span>{crumb.label}</span>
            )}
          </React.Fragment>
        ))}
        {breadcrumb.length > 0 && <span style={{ color: 'var(--college-border)' }}>/</span>}
        <span style={{ color: 'var(--college-ink)' }}>{essayTitle(essay)}</span>
      </div>

      {/* ── Head ──────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
          borderBottom: '2px solid var(--college-ink)',
          paddingBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Eyebrow>
            {[school, TYPE_LABELS[essay.type], essay.wordLimit ? `${essay.wordLimit} words` : null]
              .filter(Boolean)
              .join(' · ')}
          </Eyebrow>
          <h1
            className="font-serif-editorial"
            style={{ fontSize: 38, fontWeight: 400, lineHeight: 1.05, margin: 0, color: 'var(--college-ink)' }}
          >
            {essayTitle(essay)}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          {draftUrl && (
            <DeskButton onClick={() => window.open(draftUrl, '_blank', 'noopener')}>Open draft ↗</DeskButton>
          )}
          {can.manageAccess && <DeskButton onClick={actions.goShare}>Share</DeskButton>}
          {can.markReviewed && (
            <DeskButton
              variant="dark"
              onClick={() =>
                actions.patchEssay({ waitingOn: 'NONE', waitingNote: null }).then(() => toast.success('Marked reviewed'))
              }
              disabled={!essay.waitingOn || essay.waitingOn === 'NONE'}
            >
              Mark reviewed
            </DeskButton>
          )}
          {!can.markReviewed && can.comment && (
            <DeskButton variant="dark" onClick={() => actions.focusComposer?.()}>
              Add a note
            </DeskButton>
          )}
        </div>
      </div>

      {/* ── Stage strip ───────────────────────────────────────── */}
      <div
        className="desk-stage-strip"
        style={{
          display: 'flex',
          alignItems: 'stretch',
          marginTop: 20,
          border: '1px solid var(--college-border)',
          background: 'var(--college-panel)',
        }}
      >
        <div style={{ flex: 1, padding: '14px 16px', background: 'var(--college-ink)', color: 'var(--college-bg)' }}>
          <p
            style={{
              margin: 0,
              font: "600 10px/1 'Instrument Sans', sans-serif",
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              opacity: 0.6,
            }}
          >
            {stage < 0 ? 'Not started' : `Stage ${stage + 1} of 5`}
          </p>
          <p className="font-serif-editorial" style={{ margin: '8px 0 0', fontSize: 22 }}>
            {STAGE_LABELS[essay.status]}
          </p>
        </div>

        <div style={{ flex: 1, padding: '14px 16px', borderRight: '1px solid var(--college-border-soft)' }}>
          <StripLabel>Words</StripLabel>
          <p className="font-mono-editorial" style={{ margin: '8px 0 0', fontSize: 17, color: 'var(--college-ink)' }}>
            {essay.wordCount != null ? essay.wordCount : '—'} / {essay.wordLimit != null ? essay.wordLimit : '—'}
          </p>
        </div>

        <div style={{ flex: 1, padding: '14px 16px', borderRight: '1px solid var(--college-border-soft)' }}>
          <StripLabel>Due</StripLabel>
          <p className="font-mono-editorial" style={{ margin: '8px 0 0', fontSize: 17, color: 'var(--college-ink)' }}>
            {deadline ? `${dateShort(deadline)} · ${daysUntil(deadline)}d` : '—'}
          </p>
        </div>

        {waitingCopy && (
          <div style={{ flex: 1.4, padding: '14px 16px', background: 'var(--college-red)', color: 'var(--college-bg)' }}>
            <p
              style={{
                margin: 0,
                font: "600 10px/1 'Instrument Sans', sans-serif",
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                opacity: 0.7,
              }}
            >
              Waiting on
            </p>
            <p style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 600 }}>{waitingCopy}</p>
          </div>
        )}

        <div
          style={{
            flex: 1,
            padding: '14px 16px',
            borderLeft: '1px solid var(--college-border-soft)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {can.changeStage ? (
            <>
              <label style={{ margin: 0, fontSize: 12, color: 'var(--college-muted)' }} htmlFor="desk-stage-select">
                Change stage
              </label>
              <select
                id="desk-stage-select"
                value={essay.status}
                onChange={(e) => actions.patchEssay({ status: e.target.value }, { quiet: true })}
                style={{
                  marginTop: 5,
                  padding: 0,
                  border: 'none',
                  borderBottom: '1px solid var(--college-gold)',
                  background: 'none',
                  fontFamily: 'inherit',
                  fontSize: 12.5,
                  color: 'var(--college-ink)',
                  alignSelf: 'flex-start',
                  cursor: 'pointer',
                }}
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {STAGE_LABELS[s]}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <>
              <StripLabel>Last touched</StripLabel>
              <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--college-ink-soft)' }}>
                {relativeDay(essay.updatedAt)}
              </p>
            </>
          )}
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────── */}
      <div
        className="desk-detail-grid"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 330px', gap: 28, marginTop: 30, alignItems: 'start' }}
      >
        {/* Main column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
          <div>
            <Eyebrow>The prompt</Eyebrow>
            <div
              style={{
                background: 'var(--college-panel)',
                border: '1px solid var(--college-border-soft)',
                borderLeft: '3px solid var(--college-gold)',
                padding: '16px 18px',
              }}
            >
              {essay.prompt ? (
                <p
                  className="font-serif-editorial"
                  style={{ margin: 0, fontSize: 18, lineHeight: 1.5, color: 'var(--college-ink)', textWrap: 'pretty' }}
                >
                  {essay.prompt}
                </p>
              ) : can.editDetails ? (
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--college-muted)' }}>
                  No prompt saved yet — <DeskLink onClick={() => setEditing(true)}>add it</DeskLink> so everyone
                  brainstorming is answering the same question.
                </p>
              ) : (
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--college-muted)' }}>
                  No prompt saved yet.
                </p>
              )}
            </div>
          </div>

          {/* Brainstorm */}
          {can.comment ? (
            <div>
              <SectionHead
                title="Brainstorm"
                size={24}
                meta={`${notes.length} note${notes.length === 1 ? '' : 's'} · ${noteAuthorCount || 0} ${
                  noteAuthorCount === 1 ? 'person' : 'people'
                }`}
                right={
                  <span style={{ fontSize: 12, color: 'var(--college-muted)' }}>
                    Everyone with comment access can post here
                  </span>
                }
              />
              <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)', borderTop: 'none' }}>
                {topLevelNotes.length === 0 && (
                  <p style={{ margin: 0, padding: '22px 18px', fontSize: 13.5, color: 'var(--college-muted)' }}>
                    Nothing here yet. Half-ideas count — this is where the angle usually turns up.
                  </p>
                )}
                {topLevelNotes.map((note) => (
                  <BrainstormNote
                    key={note.id}
                    note={note}
                    replies={repliesByParent.get(note.id) || []}
                    viewer={viewer}
                    can={can}
                    notifyWho={notifyWho}
                    onStar={actions.starNote}
                    onReply={actions.postNote}
                    onTurnIntoTask={actions.noteToTask}
                  />
                ))}
                <NoteComposer
                  viewer={viewer}
                  onPost={actions.postNote}
                  composerRef={actions.composerRef}
                  notifyWho={notifyWho}
                />
              </div>
            </div>
          ) : (
            <div>
              <SectionHead title="Brainstorm" size={24} />
              <div
                style={{
                  background: 'var(--college-bg-alt)',
                  border: '1px dashed var(--college-border)',
                  borderTop: 'none',
                  padding: '20px 18px',
                }}
              >
                <p style={{ margin: 0, fontSize: 13, color: 'var(--college-muted)' }}>
                  Brainstorming isn't shared with you on this desk.
                </p>
              </div>
            </div>
          )}

          {/* Tasks */}
          <div>
            <SectionHead
              title="Tasks"
              size={24}
              meta={`${openCount} open · ${doneCount} done`}
              right={
                <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <TaskChip active={taskFilter === 'ALL'} onClick={() => setTaskFilter('ALL')}>
                    All
                  </TaskChip>
                  <TaskChip active={taskFilter === 'MINE'} onClick={() => setTaskFilter('MINE')}>
                    Mine {myTasks.filter(isOpen).length}
                  </TaskChip>
                  {can.addTask && <TaskChip onClick={() => setAddingTask(true)}>+ Add task</TaskChip>}
                </span>
              }
            />
            <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)', borderTop: 'none' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: TASK_COLUMNS,
                  gap: 14,
                  alignItems: 'center',
                  padding: '10px 18px',
                  borderBottom: '1px solid var(--college-border-soft)',
                  background: 'var(--college-bg-alt)',
                }}
              >
                <span />
                {['Task', 'Assigned to', 'Due', 'Status'].map((label) => (
                  <span
                    key={label}
                    style={{
                      font: "600 10px/1 'Instrument Sans', sans-serif",
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'var(--college-muted-2)',
                    }}
                  >
                    {label}
                  </span>
                ))}
              </div>

              {shownTasks.length === 0 ? (
                <p style={{ margin: 0, padding: '18px', fontSize: 13, color: 'var(--college-muted)' }}>
                  {taskFilter === 'MINE' ? 'Nothing on your plate here.' : 'No tasks yet.'}
                </p>
              ) : (
                shownTasks.map((task, i) => {
                  const mine = (task.assignees || []).some((a) => samePerson(a, viewer));
                  // You can tick off your own work; the student can tick off
                  // anything on their own essay.
                  const canToggle = can.manageAnyTask || mine;
                  return (
                    <TaskRow
                      key={task.id}
                      task={task}
                      viewer={viewer}
                      last={i === shownTasks.length - 1}
                      onToggle={canToggle ? () => actions.setTaskStatus(task, isOpen(task) ? 'DONE' : 'TODO') : null}
                      onDecline={!can.manageAnyTask && mine && isOpen(task) ? () => actions.setTaskStatus(task, 'DECLINED') : null}
                      onDelete={can.manageAnyTask ? () => actions.deleteTask(task) : null}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div>
            <Eyebrow>{can.viewerIsOwner ? 'Shared with' : 'Who is on this'}</Eyebrow>
            <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)' }}>
              {sidebarPeople.length === 0 ? (
                <p style={{ margin: 0, padding: '14px 15px', fontSize: 12.5, color: 'var(--college-muted)' }}>
                  Nobody yet — this desk is just yours.
                </p>
              ) : (
                sidebarPeople.map((person) => (
                  <div
                    key={`${person.kind}-${person.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 11,
                      padding: '13px 15px',
                      borderBottom: '1px solid var(--college-border-soft)',
                    }}
                  >
                    <Avatar person={person} size={28} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: 'var(--college-ink)' }}>
                        {person.name}
                        {person.isYou && <span style={{ color: 'var(--college-muted)', fontWeight: 400 }}> · you</span>}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--college-muted)' }}>
                        {capabilitySummary(person, essay)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              {can.manageAccess && (
                <div style={{ padding: '11px 15px' }}>
                  <DeskLink onClick={actions.goShare}>Manage access →</DeskLink>
                </div>
              )}
            </div>
          </div>

          {can.seeChecklist && (
            <div>
              <Eyebrow>
                My checklist{' '}
                <span
                  className="font-mono-editorial"
                  style={{ letterSpacing: 0, textTransform: 'none', color: 'var(--college-muted)' }}
                >
                  private · {checklistDone}/{essay.checklistItems?.length || 0}
                </span>
              </Eyebrow>
              <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)', padding: '6px 0' }}>
                {(essay.checklistItems || []).map((item) => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 15px' }}>
                    <Checkbox
                      size={13}
                      checked={item.completed}
                      onChange={() => actions.toggleChecklist(item)}
                      label={item.label}
                    />
                    <span
                      style={{
                        fontSize: 12.5,
                        color: item.completed ? 'var(--college-muted-2)' : 'var(--college-ink)',
                        textDecoration: item.completed ? 'line-through' : 'none',
                      }}
                    >
                      {item.label}
                    </span>
                  </div>
                ))}
                {(essay.checklistItems || []).length === 0 && (
                  <p style={{ margin: 0, padding: '8px 15px', fontSize: 12.5, color: 'var(--college-muted)' }}>
                    No checklist on this one.
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <Eyebrow>Documents</Eyebrow>
            <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)' }}>
              {docs.map((doc) => (
                <DocRow
                  key={doc.id}
                  doc={doc}
                  can={can}
                  actions={actions}
                  people={people}
                />
              ))}
              {docs.length === 0 && !docPickerSlot && (
                <p style={{ margin: 0, padding: '13px 15px', fontSize: 12.5, color: 'var(--college-muted)' }}>
                  {can.readDrafts === false
                    ? 'Drafts on this essay are not shared with you.'
                    : 'No documents attached yet.'}
                </p>
              )}
              {docs.length === 0 && docPickerSlot && can.readDrafts === false && (
                <p style={{ margin: 0, padding: '13px 15px 4px', fontSize: 11.5, color: 'var(--college-muted)' }}>
                  Their drafts aren't shared with you, but you can hand over a document of your own.
                </p>
              )}
              {docPickerSlot && <div style={{ padding: '9px 15px' }}>{docPickerSlot}</div>}
            </div>
          </div>

          <div>
            <Eyebrow>
              Details{' '}
              {can.editDetails && (
                <DeskLink style={{ letterSpacing: 0, textTransform: 'none', fontSize: 11 }} onClick={() => setEditing(true)}>
                  edit
                </DeskLink>
              )}
            </Eyebrow>
            <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)' }}>
              <DetailRow label="University" value={school || '—'} />
              <DetailRow label="Platform" value={PLATFORM_LABELS[essay.platform] || '—'} />
              <DetailRow label="Type" value={TYPE_FULL_LABELS[essay.type]} />
              <DetailRow label="Word limit" value={essay.wordLimit != null ? String(essay.wordLimit) : '—'} mono />
              {can.viewerIsOwner ? (
                <DetailRow
                  label="Drafts shared"
                  value={essay.shareDrafts ? 'Yes — with anyone who may read drafts' : 'No'}
                  last
                />
              ) : (
                <DetailRow
                  label="Draft"
                  value={essay.draftsVisible ? 'Shared with you' : 'Not shared with you'}
                  last
                />
              )}
            </div>
          </div>

          {emailSlot}
        </div>
      </div>

      {editing && (
        <EditEssayModal
          essay={essay}
          onClose={() => setEditing(false)}
          onSave={async (payload) => {
            await actions.patchEssay(payload);
            setEditing(false);
          }}
        />
      )}

      {addingTask && (
        <AddTaskModal
          people={people}
          viewer={viewer}
          assignToSelfOnly={!can.assignOthers}
          ownerName={people.find((p) => p.role === 'STUDENT')?.name}
          onClose={() => setAddingTask(false)}
          onAdd={async (payload) => {
            await actions.addTask(payload);
            setAddingTask(false);
          }}
        />
      )}
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────

// One document on the essay: the CURRENT chip, the title link, who added it,
// who currently has editor access, and — when it's a Drive file the owner can
// touch — a "Share now" affordance.
function DocRow({ doc, can, actions, people }) {
  // Whose access is live according to the audit log: latest ok entry per
  // email wins, and later error entries don't unset earlier successes.
  const currentEditors = useMemo(() => {
    const log = Array.isArray(doc.sharedWith) ? doc.sharedWith : [];
    const latestPerEmail = new Map();
    log.forEach((entry) => {
      if (!entry?.email) return;
      const prev = latestPerEmail.get(entry.email);
      if (!prev || new Date(entry.at || 0) >= new Date(prev.at || 0)) latestPerEmail.set(entry.email, entry);
    });
    return Array.from(latestPerEmail.values()).filter((e) => e.ok);
  }, [doc.sharedWith]);

  const addedBy = doc.addedByGrantId
    ? people.find((p) => p.kind === 'grant' && p.id === doc.addedByGrantId)?.name || 'someone with access'
    : null;

  // Only the owner sees the "share now" action, and only for docs where we
  // know the Drive file id. A parent's paste-a-link flow leaves that null.
  const canReshare = can.viewerIsOwner && !!doc.googleDocId && typeof actions.reshareDoc === 'function';

  return (
    <div style={{ padding: '12px 15px', borderBottom: '1px solid var(--college-border-soft)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          onClick={() => can.attachDocs && !doc.isCurrent && actions.setCurrentDoc(doc.id)}
          title={doc.isCurrent ? 'This is the current draft' : can.attachDocs ? 'Make this the current draft' : doc.role}
          className="font-mono-editorial"
          style={{
            fontSize: 9.5,
            padding: '2px 6px',
            border: doc.isCurrent ? 'none' : '1px solid var(--college-border)',
            background: doc.isCurrent ? 'var(--college-ink)' : 'transparent',
            color: doc.isCurrent ? 'var(--college-bg)' : 'var(--college-muted)',
            cursor: can.attachDocs && !doc.isCurrent ? 'pointer' : 'default',
            flexShrink: 0,
          }}
        >
          {doc.isCurrent ? 'CURRENT' : doc.role}
        </button>
        <a
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1,
            fontSize: 12.5,
            color: 'var(--college-ink)',
            textDecoration: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {doc.title || 'Google Doc'}
        </a>
      </div>
      {(addedBy || currentEditors.length > 0 || canReshare) && (
        <p style={{ margin: '4px 0 0', paddingLeft: 42, fontSize: 11, color: 'var(--college-muted)', lineHeight: 1.5 }}>
          {addedBy && <span>added by {addedBy}</span>}
          {addedBy && (currentEditors.length > 0 || canReshare) && <span> · </span>}
          {currentEditors.length > 0 && (
            <span>
              editors:{' '}
              {currentEditors.slice(0, 3).map((e) => e.name || e.email).join(', ')}
              {currentEditors.length > 3 && ` and ${currentEditors.length - 3} more`}
            </span>
          )}
          {currentEditors.length > 0 && canReshare && <span> · </span>}
          {canReshare && (
            <DeskLink style={{ fontSize: 11 }} onClick={() => actions.reshareDoc(doc)}>
              {currentEditors.length === 0 ? 'Share with drafts editors' : 'Sync editors'}
            </DeskLink>
          )}
        </p>
      )}
    </div>
  );
}

function StripLabel({ children }) {
  return (
    <p
      style={{
        margin: 0,
        font: "600 10px/1 'Instrument Sans', sans-serif",
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--college-muted-2)',
      }}
    >
      {children}
    </p>
  );
}

// What somebody may do here, in the sidebar's shorthand. The student's own row
// says so plainly; everybody else is described by their capabilities.
function capabilitySummary(person, essay) {
  if (person.role === 'STUDENT') return 'Writes it';
  const bits = [];
  if (person.seeProgress) bits.push('Progress');
  if (person.readDrafts && essay.shareDrafts) bits.push('reads drafts');
  if (person.comment) bits.push('brainstorms');
  if (person.assignTasks) bits.push('assigns tasks');
  if (person.readDrafts && !essay.shareDrafts) bits.push('no drafts on this one');
  return bits.length ? bits.join(' · ') : 'Progress only';
}

function DetailRow({ label, value, mono, last }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        padding: '11px 15px',
        borderBottom: last ? 'none' : '1px solid var(--college-border-soft)',
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--college-muted)', flexShrink: 0 }}>{label}</span>
      <span
        className={mono ? 'font-mono-editorial' : undefined}
        style={{ fontSize: 12.5, color: 'var(--college-ink)', textAlign: 'right' }}
      >
        {value}
      </span>
    </div>
  );
}

function TaskChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '5px 11px',
        border: active ? 'none' : '1px solid var(--college-border)',
        background: active ? 'var(--college-ink)' : 'var(--college-panel)',
        color: active ? 'var(--college-bg)' : 'var(--college-ink-soft)',
        fontFamily: 'inherit',
        fontSize: 11.5,
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function BrainstormNote({ note, replies, viewer, can, notifyWho = [], onStar, onReply, onTurnIntoTask }) {
  const [replying, setReplying] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(false);
  const fresh = isFresh(note, viewer);
  const mine = samePerson(note.author, viewer);

  const send = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await onReply(draft, note.id, { notify });
      setDraft('');
      setNotify(false);
      setReplying(false);
      setShowReplies(true);
    } catch {
      toast.error('Failed to post that reply');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '34px minmax(0,1fr)',
        gap: 14,
        padding: 18,
        borderBottom: '1px solid var(--college-border-soft)',
        ...(note.starred ? { background: '#FBF6EA', borderLeft: '3px solid var(--college-gold)' } : null),
      }}
    >
      <Avatar person={note.author} size={30} />
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--college-ink)' }}>
            {mine ? 'You' : note.author?.name || 'Someone'}
          </span>
          {note.starred && (
            <span
              style={{
                padding: '2px 7px',
                background: 'var(--college-gold)',
                color: 'var(--college-ink)',
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              The angle
            </span>
          )}
          {!mine && note.author?.role && note.author.role !== 'STUDENT' && (
            <span
              style={{
                padding: '2px 7px',
                border: '1px solid var(--college-border)',
                fontSize: 9.5,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--college-muted)',
              }}
            >
              {ROLE_LABELS[note.author.role] || note.author.role}
            </span>
          )}
          {fresh && (
            <span
              style={{
                padding: '2px 7px',
                background: 'var(--college-bg-alt)',
                fontSize: 9.5,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--college-ink-soft)',
              }}
            >
              New
            </span>
          )}
          <span className="font-mono-editorial" style={{ fontSize: 11, color: 'var(--college-muted-2)', marginLeft: 'auto' }}>
            {relativeDay(note.createdAt)}
          </span>
        </div>

        <p
          style={{
            margin: '8px 0 0',
            fontSize: 14,
            lineHeight: 1.6,
            color: mine ? 'var(--college-ink)' : 'var(--college-ink-soft)',
            textWrap: 'pretty',
          }}
        >
          {note.body}
        </p>

        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11.5, color: 'var(--college-muted)', flexWrap: 'wrap' }}>
          <DeskLink style={{ fontSize: 11.5, color: 'var(--college-muted)' }} onClick={() => setReplying((v) => !v)}>
            Reply
          </DeskLink>
          {replies.length > 0 && (
            <DeskLink style={{ fontSize: 11.5, color: 'var(--college-muted)' }} onClick={() => setShowReplies((v) => !v)}>
              {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
            </DeskLink>
          )}
          {can.star && (
            <DeskLink
              style={{ fontSize: 11.5, color: note.starred ? 'var(--college-red)' : 'var(--college-muted)' }}
              onClick={() => onStar(note.id, !note.starred)}
            >
              {note.starred ? 'Unstar' : 'Star as the angle'}
            </DeskLink>
          )}
          {can.addTask && (
            <DeskLink style={{ fontSize: 11.5, color: 'var(--college-muted)' }} onClick={() => onTurnIntoTask(note)}>
              Turn into a task
            </DeskLink>
          )}
        </div>

        {replying && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                autoFocus
                style={{ ...inputStyle, fontSize: 13 }}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder="Reply…"
              />
              <DeskButton variant="dark" onClick={send} disabled={busy || !draft.trim()}>
                Post
              </DeskButton>
            </div>
            {draft.trim() && (
              <NotifyToggle checked={notify} onChange={setNotify} who={notifyWho} style={{ marginTop: 10 }} />
            )}
          </div>
        )}

        {showReplies && replies.length > 0 && (
          <div
            style={{
              marginTop: 14,
              paddingLeft: 14,
              borderLeft: '1px solid var(--college-border-soft)',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {replies.map((reply) => (
              <div key={reply.id} style={{ display: 'flex', gap: 10 }}>
                <Avatar person={reply.author} size={22} />
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--college-ink)' }}>
                      {samePerson(reply.author, viewer) ? 'You' : reply.author?.name}
                    </span>
                    <span className="font-mono-editorial" style={{ fontSize: 10.5, color: 'var(--college-muted-2)' }}>
                      {relativeDay(reply.createdAt)}
                    </span>
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: 13, lineHeight: 1.55, color: 'var(--college-ink-soft)' }}>
                    {reply.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NoteComposer({ viewer, onPost, composerRef, notifyWho = [] }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(false);

  const post = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await onPost(draft, null, { notify });
      setDraft('');
      setNotify(false);
    } catch {
      toast.error('Failed to post that note');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '34px minmax(0,1fr)',
        gap: 14,
        padding: '16px 18px',
        background: 'var(--college-bg)',
      }}
    >
      <span
        style={{
          width: 30,
          height: 30,
          borderRadius: 999,
          background: personColor(viewer).bg,
          color: personColor(viewer).fg,
          fontSize: 11,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {initialsOf(viewer?.name)}
      </span>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            ref={composerRef}
            style={{ ...inputStyle, flex: 1, border: 'none', background: 'transparent', padding: 0, fontSize: 13.5 }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && post()}
            placeholder="Add a note, an image, a half-idea…"
          />
          <DeskButton
            variant="dark"
            onClick={post}
            disabled={busy || !draft.trim()}
            style={{ padding: '7px 14px', fontSize: 12 }}
          >
            Post
          </DeskButton>
        </div>
        {draft.trim() && (
          <NotifyToggle checked={notify} onChange={setNotify} who={notifyWho} style={{ marginTop: 10 }} />
        )}
      </div>
    </div>
  );
}

function TaskRow({ task, viewer, last, onToggle, onDecline, onDelete }) {
  const done = task.status === 'DONE';
  const declined = task.status === 'DECLINED';
  const overdue = task.dueDate && new Date(task.dueDate) < Date.now() && !done && !declined;
  const createdByYou = samePerson(task.createdBy, viewer);
  // If it's partly yours, you come first — the row should read as your job.
  const assignees = [...(task.assignees || [])].sort(
    (a, b) => Number(samePerson(b, viewer)) - Number(samePerson(a, viewer))
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: TASK_COLUMNS,
        gap: 14,
        alignItems: 'center',
        padding: '14px 18px',
        borderBottom: last ? 'none' : '1px solid var(--college-border-soft)',
        opacity: done || declined ? 0.5 : 1,
      }}
    >
      {onToggle ? (
        <Checkbox checked={done} onChange={onToggle} label={task.title} />
      ) : (
        <span
          aria-hidden
          style={{
            width: 15,
            height: 15,
            border: '1.5px solid var(--college-border)',
            background: done ? 'var(--college-border)' : 'transparent',
          }}
        />
      )}

      <div style={{ minWidth: 0 }}>
        <span style={{ fontSize: 14, color: 'var(--college-ink)', textDecoration: done ? 'line-through' : 'none' }}>
          {task.title}
        </span>
        <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--college-muted)' }}>
          {task.createdBy ? `Added by ${createdByYou ? 'you' : task.createdBy.name}` : 'Added'}
          {task.detail ? ` · ${task.detail}` : ''}
          {onDecline && (
            <>
              {' · '}
              <DeskLink style={{ fontSize: 11.5 }} onClick={onDecline}>
                decline
              </DeskLink>
            </>
          )}
          {onDelete && (
            <>
              {' '}
              <DeskLink style={{ fontSize: 11.5 }} onClick={onDelete}>
                remove
              </DeskLink>
            </>
          )}
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
        {assignees.slice(0, 2).map((person, i) => (
          <span key={`${person.kind}-${person.id}`} style={{ marginLeft: i === 0 ? 0 : -8 }}>
            <Avatar person={person} size={22} ring={i > 0} />
          </span>
        ))}
        {assignees.length === 0 ? (
          <span style={{ fontSize: 12, color: 'var(--college-muted-2)' }}>Unassigned</span>
        ) : assignees.length > 2 ? (
          <span style={{ fontSize: 12, color: 'var(--college-ink-soft)' }}>+{assignees.length - 2}</span>
        ) : (
          <span
            style={{
              fontSize: 12,
              color: 'var(--college-ink-soft)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {samePerson(assignees[0], viewer) ? 'You' : assignees[0].name}
          </span>
        )}
      </div>

      <span
        className="font-mono-editorial"
        style={{
          fontSize: 12,
          color: overdue ? 'var(--college-red)' : task.dueDate ? 'var(--college-ink-soft)' : 'var(--college-muted-2)',
        }}
      >
        {task.dueDate ? dateShort(task.dueDate) : '—'}
      </span>

      <span
        style={{
          padding: '4px 10px',
          justifySelf: 'start',
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          ...(task.status === 'TODO'
            ? { border: '1px solid var(--college-border)', color: 'var(--college-muted)' }
            : { background: 'var(--college-bg-alt)', color: 'var(--college-ink-soft)' }),
        }}
      >
        {TASK_STATUS_LABELS[task.status]}
      </span>
    </div>
  );
}

// ── Modals ────────────────────────────────────────────────────────────────

function EditEssayModal({ essay, onClose, onSave }) {
  const [form, setForm] = useState({
    title: essay.title || '',
    school: essay.school || '',
    platform: essay.platform || '',
    prompt: essay.prompt || '',
    wordLimit: essay.wordLimit != null ? String(essay.wordLimit) : '',
    wordCount: essay.wordCount != null ? String(essay.wordCount) : '',
    waitingOn: essay.waitingOn || 'NONE',
    waitingNote: essay.waitingNote || '',
    shareDrafts: !!essay.shareDrafts,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        title: form.title || null,
        school: form.school || null,
        platform: form.platform || null,
        prompt: form.prompt || null,
        wordLimit: form.wordLimit === '' ? null : form.wordLimit,
        wordCount: form.wordCount === '' ? null : form.wordCount,
        waitingOn: form.waitingOn,
        waitingNote: form.waitingNote || null,
        shareDrafts: form.shareDrafts,
      });
    } catch {
      // the caller has already surfaced the error
    } finally {
      setSaving(false);
    }
  };

  return (
    <DeskModal eyebrow="Keep it honest" title="Essay details" onClose={onClose} width={620}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Nickname">
            <input style={inputStyle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="School / organisation">
            <input style={inputStyle} value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} />
          </Field>
        </div>

        <Field label="The prompt">
          <textarea
            style={{ ...inputStyle, minHeight: 90, resize: 'vertical', lineHeight: 1.5 }}
            value={form.prompt}
            onChange={(e) => setForm({ ...form, prompt: e.target.value })}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <Field label="Words so far">
            <input
              style={inputStyle}
              type="number"
              value={form.wordCount}
              onChange={(e) => setForm({ ...form, wordCount: e.target.value })}
              placeholder="—"
            />
          </Field>
          <Field label="Word limit">
            <input
              style={inputStyle}
              type="number"
              value={form.wordLimit}
              onChange={(e) => setForm({ ...form, wordLimit: e.target.value })}
              placeholder="—"
            />
          </Field>
          <Field label="Platform">
            <select style={inputStyle} value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
              <option value="">—</option>
              {Object.entries(PLATFORM_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Waiting on">
            <select style={inputStyle} value={form.waitingOn} onChange={(e) => setForm({ ...form, waitingOn: e.target.value })}>
              {WAITING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Waiting note" hint="Shows under the title on the hub">
            <input
              style={inputStyle}
              value={form.waitingNote}
              onChange={(e) => setForm({ ...form, waitingNote: e.target.value })}
              placeholder="e.g. Ms. Alvarez has read-through due Thu"
            />
          </Field>
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--college-ink)' }}>
          <input
            type="checkbox"
            checked={form.shareDrafts}
            onChange={(e) => setForm({ ...form, shareDrafts: e.target.checked })}
            style={{ marginTop: 2 }}
          />
          <span>
            Let people with draft access read this one
            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--college-muted)', marginTop: 3 }}>
              They still only see it if you turned drafts on for them personally.
            </span>
          </span>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 16, borderTop: '1px solid var(--college-border)' }}>
          <DeskButton onClick={onClose}>Cancel</DeskButton>
          <DeskButton variant="dark" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </DeskButton>
        </div>
      </form>
    </DeskModal>
  );
}

function AddTaskModal({ people, viewer, assignToSelfOnly, ownerName, onClose, onAdd }) {
  const [form, setForm] = useState({ title: '', detail: '', dueDate: '' });
  const [assignees, setAssignees] = useState(assignToSelfOnly ? [] : [{ kind: viewer.kind, id: viewer.id }]);
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState(false);

  const toggle = (person) => {
    setAssignees((prev) => {
      const has = prev.some((a) => a.kind === person.kind && a.id === person.id);
      return has ? prev.filter((a) => !(a.kind === person.kind && a.id === person.id)) : [...prev, person];
    });
  };

  // A grant can only ask the student for something, so there is nobody to pick.
  const candidates = assignToSelfOnly
    ? []
    : people.filter((p) => p.role === 'STUDENT' || p.comment || p.assignTasks);

  const student = people.find((p) => p.role === 'STUDENT');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await onAdd({ ...form, dueDate: form.dueDate || null, notify, ...(assignToSelfOnly ? {} : { assignees }) });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add that task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DeskModal
      eyebrow="Someone has to do it"
      title={assignToSelfOnly ? `Ask ${student?.name || 'them'} for something` : 'Add a task'}
      onClose={onClose}
      width={540}
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Field label="What needs doing">
          <input
            autoFocus
            style={inputStyle}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Rewrite the opening around the intake sheet"
          />
        </Field>

        {assignToSelfOnly ? (
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--college-muted)' }}>
            This lands on {student?.name || 'the student'}'s list. They can mark it done — or decline it, and you'll
            see that either way.
          </p>
        ) : (
          <Field label="Assigned to" hint="Anyone you've given access to can be asked — they can decline">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {candidates.map((person) => {
                const active = assignees.some((a) => a.kind === person.kind && a.id === person.id);
                const { bg } = personColor(person);
                const isSelf = person.kind === viewer.kind && person.id === viewer.id;
                return (
                  <button
                    key={`${person.kind}-${person.id}`}
                    type="button"
                    onClick={() => toggle({ kind: person.kind, id: person.id })}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      padding: '6px 11px',
                      border: active ? '1px solid var(--college-ink)' : '1px solid var(--college-border)',
                      background: active ? 'var(--college-bg-alt)' : 'var(--college-panel)',
                      fontFamily: 'inherit',
                      fontSize: 12.5,
                      color: 'var(--college-ink)',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: bg }} />
                    {isSelf ? 'You' : person.name}
                  </button>
                );
              })}
            </div>
          </Field>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 14 }}>
          <Field label="Note">
            <input
              style={inputStyle}
              value={form.detail}
              onChange={(e) => setForm({ ...form, detail: e.target.value })}
              placeholder="Optional context"
            />
          </Field>
          <Field label="Due">
            <input
              style={inputStyle}
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
            />
          </Field>
        </div>

        <div style={{ paddingTop: 16, borderTop: '1px solid var(--college-border)' }}>
          <NotifyToggle
            checked={notify}
            onChange={setNotify}
            who={
              assignToSelfOnly
                ? [ownerName || 'them']
                : assignees
                    .filter((a) => !(a.kind === viewer.kind && a.id === viewer.id))
                    .map((a) => people.find((p) => p.kind === a.kind && p.id === a.id)?.name)
                    .filter(Boolean)
            }
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <DeskButton onClick={onClose}>Cancel</DeskButton>
            <DeskButton variant="dark" type="submit" disabled={saving || !form.title.trim()}>
              {saving ? 'Adding…' : assignToSelfOnly ? 'Send it' : 'Add task'}
            </DeskButton>
          </div>
        </div>
      </form>
    </DeskModal>
  );
}
